import { Injectable } from '@angular/core';
import { PendingChange, ChangeOperation } from '../../data/interfaces/PendingChange';
import { ClinicService } from '../../networking/services/clinic.service';
import { ContentBlockService } from '../../networking/services/content-block.service';
import { firstValueFrom } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ChangeTrackerService {
  private changes: PendingChange[] = []

  addChange(change: PendingChange): void {
    // DELETE nach CREATE fuer denselben Block: beide entfernen
    if (change.operation === 'DELETE_BLOCK' && change.blockKey) {
      const createIndex = this.changes.findIndex(c => c.operation === 'CREATE_BLOCK' && c.tempKey === change.blockKey)
      if (createIndex >= 0) {
        this.changes.splice(createIndex, 1)
        this.changes = this.changes.filter(c => c.blockKey !== change.blockKey && c.tempKey !== change.blockKey)
        return
      }
    }

    // Mehrere UPDATE_BLOCK fuer denselben Block: letzte gewinnt
    if (change.operation === 'UPDATE_BLOCK' && change.blockKey) {
      this.changes = this.changes.filter(c => !(c.operation === 'UPDATE_BLOCK' && c.blockKey === change.blockKey))
    }

    // UPDATE_CLINIC: nur eine behalten
    if (change.operation === 'UPDATE_CLINIC') {
      this.changes = this.changes.filter(c => c.operation !== 'UPDATE_CLINIC')
    }

    // UPLOAD_LOGO: nur eine behalten
    if (change.operation === 'UPLOAD_LOGO') {
      this.changes = this.changes.filter(c => c.operation !== 'UPLOAD_LOGO')
    }

    // TOGGLE_PUBLISH_BLOCK: gerade Anzahl = entfernen
    if (change.operation === 'TOGGLE_PUBLISH_BLOCK' && change.blockKey) {
      const existingIndex = this.changes.findIndex(
        c => c.operation === 'TOGGLE_PUBLISH_BLOCK' && c.blockKey === change.blockKey
      )
      if (existingIndex >= 0) {
        this.changes.splice(existingIndex, 1)
        return
      }
    }

    // TOGGLE_COLLAPSIBLE_BLOCK: gerade Anzahl = entfernen
    if (change.operation === 'TOGGLE_COLLAPSIBLE_BLOCK' && change.blockKey) {
      const existingIndex = this.changes.findIndex(
        c => c.operation === 'TOGGLE_COLLAPSIBLE_BLOCK' && c.blockKey === change.blockKey
      )
      if (existingIndex >= 0) {
        this.changes.splice(existingIndex, 1)
        return
      }
    }

    // REORDER_BLOCK: nur letzte pro Block behalten
    if (change.operation === 'REORDER_BLOCK' && change.blockKey) {
      this.changes = this.changes.filter(c => !(c.operation === 'REORDER_BLOCK' && c.blockKey === change.blockKey))
    }

    this.changes.push(change)
  }

  hasChanges(): boolean {
    return this.changes.length > 0
  }

  getChanges(): PendingChange[] {
    return [...this.changes]
  }

  clear(): void {
    this.changes = []
  }

  async applyAll(clinicService: ClinicService, contentBlockService: ContentBlockService): Promise<void> {
    const orderedOps: ChangeOperation[] = [
      'UPDATE_CLINIC', 'UPLOAD_LOGO', 'DELETE_BLOCK', 'CREATE_BLOCK',
      'UPDATE_BLOCK', 'REORDER_BLOCK', 'TOGGLE_PUBLISH_BLOCK', 'TOGGLE_COLLAPSIBLE_BLOCK'
    ]

    const keyMap = new Map<string, string>()

    for (const opType of orderedOps) {
      const opsOfType = this.changes.filter(c => c.operation === opType)

      for (const op of opsOfType) {
        const resolvedKey = op.blockKey ? (keyMap.get(op.blockKey) ?? op.blockKey) : undefined

        switch (op.operation) {
          case 'UPDATE_CLINIC':
            await firstValueFrom(clinicService.updateClinic(op.payload))
            break
          case 'UPLOAD_LOGO':
            if (op.file) await firstValueFrom(clinicService.uploadLogo(op.file))
            break
          case 'DELETE_BLOCK':
            if (resolvedKey) await firstValueFrom(contentBlockService.deleteBlock(resolvedKey))
            break
          case 'CREATE_BLOCK': {
            const created = await firstValueFrom(contentBlockService.createBlock(op.payload))
            if (op.tempKey) keyMap.set(op.tempKey, created.key)
            if (op.file) await firstValueFrom(contentBlockService.uploadMedia(created.key, op.file, op.previewFile))
            break
          }
          case 'UPDATE_BLOCK':
            if (resolvedKey) {
              await firstValueFrom(contentBlockService.updateBlock(resolvedKey, op.payload))
              if (op.file) await firstValueFrom(contentBlockService.uploadMedia(resolvedKey, op.file, op.previewFile))
            }
            break
          case 'REORDER_BLOCK':
            if (resolvedKey && op.payload?.order !== undefined) {
              await firstValueFrom(contentBlockService.reorderBlock(resolvedKey, op.payload.order))
            }
            break
          case 'TOGGLE_PUBLISH_BLOCK':
            if (resolvedKey) await firstValueFrom(contentBlockService.togglePublishBlock(resolvedKey))
            break
          case 'TOGGLE_COLLAPSIBLE_BLOCK':
            if (resolvedKey) await firstValueFrom(contentBlockService.toggleCollapsibleBlock(resolvedKey))
            break
        }
      }
    }

    this.clear()
  }
}
